/*
 * Tripventura — Land of Legends interactive booking flow.
 * Hotel autocomplete -> transfer zone -> date + participants -> live package totals
 * -> /cart/add.js (per-tier line items + Booking Date / Starting Hour props) -> /checkout.
 *
 * Reads config from a JSON island (<script type="application/json" data-lotl-config>).
 * No booking-engine reuse; replicates its date/season/variant rules (see docs/lotl-landing).
 */
(function () {
  'use strict';

  // canonical zone token -> matcher fragments (mirrors tools/places-proxy ZONE_RULES for the offline area fallback)
  var ZONE_TOKENS = {
    belek: ['belek','kadriye','bogazkent','serik'],
    side: ['side','manavgat','colakli','kizilot','kumkoy','evrenseki','titreyengol','gundogdu','sorgun'],
    kemer: ['kemer','beldibi','goynuk','camyuva','tekirova','kiris','cirali','olympos'],
    alanya: ['alanya','mahmutlar','konakli','avsallar','incekum','okurcalar','turkler','oba','tosmur','kestel','kargicak','gazipasa','payallar'],
    antalya: ['lara','kundu','muratpasa','konyaalti','kaleici','kepez','aksu','antalya']
  };

  function norm(s) {
    return (s || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/ı/g,'i').replace(/ş/g,'s').replace(/ğ/g,'g').replace(/ç/g,'c').replace(/ö/g,'o').replace(/ü/g,'u');
  }
  function zoneFromText(text) {
    var words = {};
    norm(text).split(/[^a-z0-9]+/).forEach(function (w) { if (w) words[w] = 1; });
    var order = ['belek','side','kemer','alanya','antalya'];
    for (var i = 0; i < order.length; i++) {
      var toks = ZONE_TOKENS[order[i]];
      for (var j = 0; j < toks.length; j++) if (words[toks[j]]) return order[i];
    }
    return null;
  }
  function money(cents) {
    var fmt = (window.LOTL_MONEY_FORMAT || '${{amount}}').replace(/<[^>]+>/g, '');
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    var withDec = parts.join('.'), noDec = parts[0];
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withDec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ',').replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec.replace(/,/g, '.'));
  }
  function ymd(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }
  function pretty(d) {
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // ---- per-product model -------------------------------------------------
  function Product(cfg) {
    this.cfg = cfg;
    this.meta = cfg.meta || {};
    // available_months / available_days arrive from Liquid as language-independent
    // numeric indices (months 0–11 -> getMonth(), days 0–6 Sun=0 -> getDay()), so date
    // matching no longer depends on the storefront language. Coerce + range-guard to
    // ignore any stray/unmapped value (an unrecognised entry simply relaxes the rule
    // rather than disabling the calendar).
    this.months = (this.meta.available_months || []).map(Number).filter(function (n) { return n >= 0 && n <= 11; });
    this.days = (this.meta.available_days || []).map(Number).filter(function (n) { return n >= 0 && n <= 6; });
    this.excluded = (this.meta.excluded_dates || []).map(function (d) { return ymd(new Date(d)); });
    this.cutoffHrs = Number(this.meta.cutoff_time || 0);
    this.pickupHour = cfg.pickupHour || '';
    // season ranges (P1): [{startM,endM,value}]
    this.seasons = [];
    if (cfg.seasonOptionIndex != null && cfg.seasonOptionIndex >= 0) {
      var seen = {};
      cfg.variants.forEach(function (v) {
        var val = v.options[cfg.seasonOptionIndex];
        if (!val || seen[val]) return; seen[val] = 1;
        var mm = val.split('-');
        var sM = parseInt((mm[0] || '').split('.')[1], 10);
        var eM = parseInt((mm[1] || '').split('.')[1], 10);
        if (sM) this.seasons.push({ startM: sM, endM: eM || sM, value: val });
      }, this);
    }
    // canonical zone -> this product's zone option value.
    // Prefer the map Liquid resolved (it recognises every localized/translated place-name
    // form, incl. Cyrillic and the "Side"->"Taraf"/"Сторона" auto-translations that
    // zoneFromText below cannot). Fall back to client-side parsing only if it's absent.
    this.zoneValues = {};
    if (cfg.zoneValues && typeof cfg.zoneValues === 'object' && Object.keys(cfg.zoneValues).length) {
      this.zoneValues = cfg.zoneValues;
    } else {
      var zi = cfg.zoneOptionIndex;
      cfg.variants.forEach(function (v) {
        var raw = v.options[zi];
        var z = zoneFromText(raw);
        if (z && !this.zoneValues[z]) this.zoneValues[z] = raw;
      }, this);
    }
  }
  Product.prototype.seasonFor = function (date) {
    if (!this.seasons.length) return undefined;          // not season-priced
    var m = date.getMonth() + 1;
    for (var i = 0; i < this.seasons.length; i++) {
      if (m >= this.seasons[i].startM && m <= this.seasons[i].endM) return this.seasons[i].value;
    }
    return null;                                          // month has no season -> unbookable
  };
  Product.prototype.minDate = function () {
    return new Date(Date.now() + this.cutoffHrs * 3600 * 1000);
  };
  Product.prototype.isBookable = function (date) {
    if (date < this.stripTime(this.minDate())) return false;
    if (this.excluded.indexOf(ymd(date)) !== -1) return false;
    if (this.months.length && this.months.indexOf(date.getMonth()) === -1) return false;
    if (this.days.length && this.days.indexOf(date.getDay()) === -1) return false;
    if (this.seasons.length && this.seasonFor(date) == null) return false;
    return true;
  };
  Product.prototype.stripTime = function (d) { var x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  Product.prototype.nextBookable = function (from) {
    var d = this.stripTime(from < this.minDate() ? this.minDate() : from);
    for (var i = 0; i < 400; i++) { if (this.isBookable(d)) return new Date(d); d.setDate(d.getDate() + 1); }
    return null;
  };
  // variant id + price for (zone, tierValue, date-derived season)
  Product.prototype.variant = function (zone, tierValue, date) {
    var cfg = this.cfg, zv = this.zoneValues[zone];
    if (!zv) return null;
    var season = this.seasonFor(date);
    if (season === null) return null;
    for (var i = 0; i < cfg.variants.length; i++) {
      var v = cfg.variants[i];
      if (v.options[cfg.zoneOptionIndex] !== zv) continue;
      if (v.options[cfg.participantsIndex] !== tierValue) continue;
      if (season !== undefined && v.options[cfg.seasonOptionIndex] !== season) continue;
      return v;
    }
    return null;
  };
  // total for a date + counts {Adult:n,...}; returns {cents, lines:[{id,qty}], date}
  Product.prototype.quote = function (zone, counts, date) {
    var effDate = this.isBookable(date) ? date : this.nextBookable(date);
    if (!effDate) return null;
    var cents = 0, lines = [], ok = true;
    this.cfg.tiers.forEach(function (tier) {
      var n = counts[tier.key] || 0; if (n <= 0) return;
      var v = this.variant(zone, tier.value, effDate);
      if (!v) { ok = false; return; }
      cents += v.price * n;
      lines.push({ id: v.id, quantity: n });
    }, this);
    if (!ok || !lines.length) return null;
    return { cents: cents, lines: lines, date: effDate, snapped: ymd(effDate) !== ymd(date) };
  };

  // ---- the element -------------------------------------------------------
  function LotlBooking(root) {
    this.root = root;
    var island = root.querySelector('[data-lotl-config]');
    this.cfg = JSON.parse(island.textContent);
    this.products = this.cfg.products.map(function (p) { return new Product(p); });
    var ep = (this.cfg.placesEndpoint || '').trim();
    this.endpoint = /^https?:\/\/[^\/\s]+/.test(ep) ? ep.replace(/\/$/, '') : '';
    this.session = String(Date.now()) + Math.random().toString(36).slice(2);
    this.state = { zone: null, zoneLabel: '', date: null, counts: { adult: 2, child: 0, infant: 0 } };
    this._seq = 0; this.activeIdx = -1; this._items = [];
    this.$ = function (s) { return root.querySelector(s); };
    this.$$ = function (s) { return Array.prototype.slice.call(root.querySelectorAll(s)); };
    this.bind();
    this.applyDeepLink();
  }

  LotlBooking.prototype.bind = function () {
    var self = this;
    // --- hotel autocomplete ---
    var input = this.$('[data-hotel-input]');
    var list = this.$('[data-suggestions]');
    if (input) {
      input.addEventListener('input', debounce(function () { self.search(input.value); }, 180));
      input.addEventListener('focus', function () { if (list.children.length) list.hidden = false; });
      input.addEventListener('keydown', function (e) { self.onKey(e); });
      // show/hide the clear (×) button as the field gains/loses text
      input.addEventListener('input', function () { self.syncClear(); });
      var clearBtn = this.$('[data-hotel-clear]');
      if (clearBtn) clearBtn.addEventListener('click', function () { self.clearSearch(); });
      input.setAttribute('role', 'combobox'); input.setAttribute('aria-autocomplete', 'list');
      // Close the dropdown only on a click/tap outside the "safe" container:
      // desktop -> the outer search card; mobile (<=749px) -> the whole hero section.
      // NOTE: `root` is a constructor param, not visible here — use `this.root`.
      var card = input.closest('.lotl-card') || this.root;
      var hero = this.root.closest('.lotl-hero') || card;
      function safeArea() {
        var isMobile = window.matchMedia('(max-width: 749px)').matches;
        return isMobile ? hero : card;
      }
      function closeIfOutside(e) {
        var area = safeArea();
        var inside = area.contains(e.target);
        // console.log('[lotl] outside-check', { isMobile: window.matchMedia('(max-width: 749px)').matches, area: area.className, target: e.target, inside: inside });
        if (!inside) list.hidden = true;
      }
      document.addEventListener('click', closeIfOutside);
      document.addEventListener('touchstart', closeIfOutside, { passive: true });
    }
    // --- manual area picker ---
    this.$$('[data-area]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.setZone(btn.dataset.area, btn.textContent.trim(), btn.textContent.trim()); });
    });
    // --- geolocation ---
    var geo = this.$('[data-geo]');
    if (geo) geo.addEventListener('click', function () { self.geolocate(); });
    // --- change pickup ---
    var change = this.$('[data-change-zone]');
    if (change) change.addEventListener('click', function () { self.step(1); self.$('[data-hotel-input]').focus(); });
    // --- counters ---
    this.$$('[data-counter]').forEach(function (wrap) {
      var key = wrap.dataset.counter;
      wrap.querySelector('[data-dec]').addEventListener('click', function () { self.bump(key, -1, wrap); });
      wrap.querySelector('[data-inc]').addEventListener('click', function () { self.bump(key, 1, wrap); });
    });
    // --- date (air-datepicker if present, else native) ---
    this.initDate();
  };

  LotlBooking.prototype.step = function (n) {
    // reveal steps progressively
    this.$$('[data-step]').forEach(function (el) {
      var s = Number(el.dataset.step);
      el.classList.toggle('is-active', s === n);
      el.classList.toggle('is-done', s < n);
      if (s <= n) el.hidden = false;
    });
    this.current = n;
  };

  // ---- step 1: pickup ----------------------------------------------------
  LotlBooking.prototype.setSearching = function (on) {
    var f = this.$('.lotl-search__field'); if (f) f.classList.toggle('is-searching', !!on);
  };
  // Reflect the clear (×) button visibility against the current input value.
  LotlBooking.prototype.syncClear = function () {
    var btn = this.$('[data-hotel-clear]'); if (!btn) return;
    var input = this.$('[data-hotel-input]');
    btn.hidden = !(input && input.value);
  };
  // Reset the search field + suggestions (does not touch an already-picked zone).
  LotlBooking.prototype.clearSearch = function () {
    var input = this.$('[data-hotel-input]'); if (input) input.value = '';
    var list = this.$('[data-suggestions]');
    if (list) { list.hidden = true; list.innerHTML = ''; }
    this._items = []; this.activeIdx = -1;
    this.setSearching(false);
    this.syncClear();
    if (input) input.focus();
  };
  LotlBooking.prototype.search = function (q) {
    var self = this, list = this.$('[data-suggestions]');
    if (!q || q.trim().length < 2) { list.hidden = true; list.innerHTML = ''; this.setSearching(false); return; }
    if (!this.endpoint) { this.renderSuggestions([], true); return; }
    var seq = ++this._seq;
    this.setSearching(true);
    list.innerHTML = '<div class="lotl-sg__empty lotl-sg__loading">' + esc(this.cfg.t.searching || 'Searching…') + '</div>';
    list.hidden = false;
    fetch(this.endpoint + '/autocomplete?q=' + encodeURIComponent(q) + '&session=' + this.session)
      .then(function (r) { return r.json(); })
      .then(function (d) { if (seq !== self._seq) return; self.setSearching(false); self.renderSuggestions(d.suggestions || [], false); })
      .catch(function () { if (seq !== self._seq) return; self.setSearching(false); self.renderSuggestions([], true); });
  };
  LotlBooking.prototype.renderSuggestions = function (items, failed) {
    var self = this, list = this.$('[data-suggestions]');
    list.innerHTML = ''; this.activeIdx = -1; this._items = items || [];
    if (!items.length) {
      var li = document.createElement('div');
      li.className = 'lotl-sg__empty';
      li.innerHTML = failed ? this.cfg.t.searchUnavailable : this.cfg.t.noResults;
      list.appendChild(li); list.hidden = false; return;
    }
    list.setAttribute('role', 'listbox');
    items.forEach(function (it) {
      var el = document.createElement('button');
      el.type = 'button'; el.className = 'lotl-sg__item'; el.setAttribute('role', 'option');
      el.innerHTML = '<span class="lotl-sg__main">' + esc(it.main) + '</span>' +
        (it.secondary ? '<span class="lotl-sg__sec">' + esc(it.secondary) + '</span>' : '');
      el.addEventListener('click', function () { self.pick(it); });
      list.appendChild(el);
    });
    list.hidden = false;
  };
  LotlBooking.prototype.onKey = function (e) {
    var list = this.$('[data-suggestions]');
    if (list.hidden) return;
    var opts = this.$$('[data-suggestions] .lotl-sg__item');
    if (e.key === 'Escape') { list.hidden = true; return; }
    if (!opts.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIdx = (this.activeIdx + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
      var ai = this.activeIdx;
      opts.forEach(function (o, i) { o.classList.toggle('is-active', i === ai); });
      opts[ai].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.activeIdx >= 0 && this._items[this.activeIdx]) {
      e.preventDefault(); this.pick(this._items[this.activeIdx]);
    }
  };
  LotlBooking.prototype.pick = function (it) {
    var self = this, field = this.$('.lotl-search__field');
    this.$('[data-hotel-input]').value = it.main;
    this.syncClear();
    this.$('[data-suggestions]').hidden = true;
    if (!this.endpoint) return;
    if (field) field.classList.add('is-searching');
    fetch(this.endpoint + '/resolve?placeId=' + encodeURIComponent(it.placeId) + '&session=' + this.session)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (field) field.classList.remove('is-searching');
        if (d.zone) self.setZone(d.zone, d.name || it.main, d.zoneLabel || d.area);
        else self.outOfZone(it.main);
      })
      .catch(function () { if (field) field.classList.remove('is-searching'); self.outOfZone(it.main); });
  };
  LotlBooking.prototype.outOfZone = function (label) {
    var box = this.$('[data-ooz]');
    if (box) { box.hidden = false; box.querySelector('[data-ooz-name]').textContent = label || ''; }
  };
  LotlBooking.prototype.geolocate = function () {
    var self = this, btn = this.$('[data-geo]');
    if (!navigator.geolocation || !this.endpoint) { this.nudgeAreas(); return; }
    btn.classList.add('is-loading');
    navigator.geolocation.getCurrentPosition(function (pos) {
      fetch(self.endpoint + '/reverse?lat=' + pos.coords.latitude + '&lng=' + pos.coords.longitude)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          btn.classList.remove('is-loading');
          if (d.zone) self.setZone(d.zone, self.zoneLabelOf(d.zone), d.zoneLabel || d.area);
          else self.nudgeAreas();
        })
        .catch(function () { btn.classList.remove('is-loading'); self.nudgeAreas(); });
    }, function () { btn.classList.remove('is-loading'); self.nudgeAreas(); }, { timeout: 8000 });
  };
  LotlBooking.prototype.nudgeAreas = function () {
    var alt = this.$('.lotl-search__alt');
    if (!alt) return;
    alt.style.transition = 'box-shadow .3s'; alt.style.boxShadow = '0 0 0 3px rgba(228,1,15,.25)';
    setTimeout(function () { alt.style.boxShadow = 'none'; }, 1200);
  };
  LotlBooking.prototype.setZone = function (zone, hotelLabel, areaLabel) {
    this.state.zone = zone;
    this.state.zoneLabel = areaLabel || zone;
    var chip = this.$('[data-zone-chip]');
    if (chip) {
      chip.hidden = false;
      chip.querySelector('[data-zone-name]').textContent = hotelLabel || areaLabel;
      chip.querySelector('[data-zone-area]').textContent = areaLabel || '';
    }
    var ooz = this.$('[data-ooz]'); if (ooz) ooz.hidden = true;
    this.step(2);
    this.refresh();
  };

  // ---- step 2: date + participants --------------------------------------
  LotlBooking.prototype.initDate = function () {
    var self = this, input = this.$('[data-date-input]');
    if (!input) return;
    // earliest + enabled-day test across the union of products
    var minD = new Date(Math.min.apply(null, this.products.map(function (p) { return p.minDate().getTime(); })));
    function anyBookable(date) { return self.products.some(function (p) { return p.isBookable(date); }); }
    if (window.AirDatepicker) {
      var EN_LOCALE = {
        days: ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'],
        daysShort: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'],
        daysMin: ['Su','Mo','Tu','We','Th','Fr','Sa'],
        months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
        monthsShort: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
        today: 'Today', clear: 'Clear', dateFormat: 'yyyy-MM-dd', timeFormat: '', firstDay: 1
      };
      this.dp = new AirDatepicker(input, {
        autoClose: true, minDate: minD, dateFormat: 'yyyy-MM-dd', locale: EN_LOCALE, prevHtml: '‹', nextHtml: '›',
        onRenderCell: function (o) {
          if (o.cellType !== 'day') return;
          if (!anyBookable(o.date)) return { disabled: true };
        },
        onSelect: function (o) { if (o.date) self.setDate(o.date); }
      });
    } else {
      input.type = 'date';
      input.min = ymd(minD);
      input.addEventListener('change', function () { if (input.value) self.setDate(new Date(input.value + 'T00:00:00')); });
    }
  };
  LotlBooking.prototype.setDate = function (d) {
    this.state.date = d;
    var lbl = this.$('[data-date-label]'); if (lbl) lbl.textContent = pretty(d);
    this.refresh();
  };
  LotlBooking.prototype.bump = function (key, delta, wrap) {
    var min = key === 'adult' ? 1 : 0;
    var n = Math.max(min, (this.state.counts[key] || 0) + delta);
    this.state.counts[key] = n;
    wrap.querySelector('[data-count]').textContent = n;
    this.refresh();
  };

  // ---- step 3: packages --------------------------------------------------
  LotlBooking.prototype.refresh = function () {
    var s = this.state;
    var ready = s.zone && s.date && s.counts.adult >= 1;
    var cards = this.$('[data-packages]');
    if (!ready) { if (cards) cards.classList.remove('is-ready'); return; }
    this.step(3);
    cards.classList.add('is-ready');
    var self = this;
    this.products.forEach(function (p, i) {
      var card = self.$('[data-package="' + p.cfg.key + '"]');
      if (!card) return;
      var countMap = { adult: s.counts.adult, child: s.counts.child, infant: s.counts.infant };
      var quote = p.quote(s.zone, countMap, s.date);
      var priceEl = card.querySelector('[data-price]');
      var noteEl = card.querySelector('[data-note]');
      var btn = card.querySelector('[data-book]');
      if (!quote) {
        card.classList.add('is-unavailable');
        priceEl.textContent = self.cfg.t.unavailableZone;
        noteEl.textContent = '';
        btn.disabled = true;
        return;
      }
      card.classList.remove('is-unavailable');
      priceEl.textContent = money(quote.cents);
      btn.disabled = false;
      card.dataset.lines = JSON.stringify(quote.lines);
      card.dataset.bookDate = ymd(quote.date);
      card.dataset.pickup = p.pickupHour;
      noteEl.textContent = quote.snapped
        ? self.cfg.t.dateSnapped.replace('{date}', pretty(quote.date))
        : '';
    });
  };

  LotlBooking.prototype.book = function (card) {
    var lines = JSON.parse(card.dataset.lines || '[]');
    if (!lines.length) return;
    var props = {};
    props[this.cfg.t.propDate || 'Booking Date'] = card.dataset.bookDate;
    props[this.cfg.t.propHour || 'Starting Hour'] = card.dataset.pickup;
    var items = lines.map(function (l) { return { id: l.id, quantity: l.quantity, properties: props }; });
    var btn = card.querySelector('[data-book]'); btn.classList.add('is-loading'); btn.disabled = true;
    fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function () { btn.classList.remove('is-loading'); btn.disabled = false; alert('Sorry, something went wrong adding to cart. Please try again.'); });
  };

  // ---- deep link (?pickup=alanya&date=YYYY-MM-DD&adults=2&children=1) -----
  LotlBooking.prototype.applyDeepLink = function () {
    var p = new URLSearchParams(location.search);
    var pickup = norm(p.get('pickup') || '');
    var zone = ['antalya','belek','side','kemer','alanya'].indexOf(pickup) !== -1 ? pickup : zoneFromText(pickup);
    if (p.get('adults')) this.setCount('adult', parseInt(p.get('adults'), 10));
    if (p.get('children')) this.setCount('child', parseInt(p.get('children'), 10));
    if (p.get('infants')) this.setCount('infant', parseInt(p.get('infants'), 10));
    if (zone) this.setZone(zone, this.zoneLabelOf(zone), this.zoneLabelOf(zone));
    if (p.get('date')) { var d = new Date(p.get('date') + 'T00:00:00'); if (!isNaN(d)) { if (this.dp) this.dp.selectDate(d); this.setDate(d); } }
  };
  LotlBooking.prototype.zoneLabelOf = function (z) { return z.charAt(0).toUpperCase() + z.slice(1); };
  LotlBooking.prototype.setCount = function (key, n) {
    if (isNaN(n)) return;
    this.state.counts[key] = Math.max(key === 'adult' ? 1 : 0, n);
    var wrap = this.root.querySelector('[data-counter="' + key + '"]');
    if (wrap) wrap.querySelector('[data-count]').textContent = this.state.counts[key];
  };

  function init() {
    document.querySelectorAll('[data-lotl-booking]').forEach(function (root) {
      if (root.__lotl) return; root.__lotl = true;
      var inst = new LotlBooking(root);
      root.addEventListener('click', function (e) {
        var b = e.target.closest('[data-book]'); if (b) inst.book(b.closest('[data-package]'));
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
